import _ from "lodash";
import React from "react";
import GooglePickerChooser from "./GooglePickerChooser";
import PropTypes from "prop-types";
import { compose } from "lib/apolloClient";
import { GoogleDriveIcon } from "SvgComponents";
import I18nHOC from "UIComponents/I18nHOC";
import {
  GOOGLE_DEVELOPER_KEY,
  GOOGLE_CLIENT_ID,
  PICKER_DIRECT_DOWNLOADABLE_TYPES,
  colors,
  BLOCKED_FILE_EXTENSIONS,
  BLOCKED_FILE_MIME_TYPES,
} from "Constants";
import {
  generateRandomId,
  getFileType,
  getFileName,
  getImageDimensions,
  getExportableFileUrl,
  getGoogleFileDetails,
  getMaxSize,
  getMaxSizeForVideoFiles,
  getMaxSizeForZipFiles,
  getUpdatedMaxSize,
  getFileTypesAndMaxSizes,
} from "Utils";
import {
  uploadFile,
  setProgress,
  downloadFromUrl,
  removeProgressElement,
  exceededSizeError,
  heicToJpg,
  unsupportedFileExtensionError,
} from "modules/Services";
import { connect } from "react-redux";
import DriveShareModal from "../DriveShareModal";
import { getCurriculumProgramFreeStatus } from "Platform/modules/PlatformModule";
import ACLStore from "lib/aclStore";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { Sentry } from "services/sentry";
import {
  getFileExtensionFromFilename,
  getMimeTypeFromFileName,
  getFileExtensionFromMimeType,
  ARCHIVE_MIME_TYPES,
} from "Utils/AttachmentHelpers";
import { is500MBVideoUploadLimitEnabled } from "Post/utils";

const START_PROGRESS = 50;

class GooglePicker extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = { accessToken: "", files: [], showShareModal: false };
  }

  updateAccessToken = ({ accessToken }) => {
    this.setState({ accessToken });
  };

  updateFileCount(count, isError = false) {
    const { updateFilesCount } = this.props;

    if (updateFilesCount) {
      updateFilesCount(count, isError);
    }
  }

  getSendObject = async ({ file, item, isLink }) => {
    const isFileNotEmpty = !_.isEmpty(file);
    const fileType = _.get(file, "type", "");
    const mimeType = _.get(file, "mimeType", fileType);
    const url = isLink && isFileNotEmpty ? file.url : item;
    const attachmentType =
      this.shouldExportAsLink(mimeType) || isLink
        ? "LINK"
        : getFileType(mimeType).text;
    const dimensions = await getImageDimensions({ url, type: attachmentType });
    const metadata = {
      size: _.get(file, "size", 0),
      ...dimensions,
      serviceType: "GOOGLE",
      fileType: _.get(file, "type", 0),
    };
    if (isFileNotEmpty && file.permission) {
      metadata.permission = file.permission;
    }
    return {
      type: attachmentType,
      url: url,
      name: getFileName(_.get(file, "name", ""), mimeType),
      mimeType: mimeType,
      metadata,
    };
  };

  async onFileDownloadedFromDrive({ file, uploadId, startProgress }) {
    const {
      onContentLoaded,
      onFailedAllowedMimeType,
      shouldUpload,
      parentType,
      parentId,
      portalType,
      uploadFile,
      onContentUploaded,
      onSingleContentUploaded,
      removeProgressElement,
      heicToJpg,
      setToastMsg,
    } = this.props;
    let updatedFile = file;
    if (
      ACLStore.can("FeatureFlag:EnableHEICConversion") &&
      _.includes(["image/heic", "image/heif"], _.get(file, "type"))
    ) {
      try {
        updatedFile = await heicToJpg({ file, uploadId, startProgress: 0 });
      } catch (e) {
        Sentry.captureMessage("HEIC_CONVERSION_ISSUE_", {
          extra: {
            functionName: "heicToJpg",
            reason: _.get(e, "message"),
            fileType: _.get(file, "type"),
            component: "GooglePicker",
          },
        });
      }
    }

    if (onContentLoaded) {
      // Handle your own upload

      if (!_.isEmpty(updatedFile)) {
        const url = URL.createObjectURL(updatedFile);
        const sendObj = await this.getSendObject({
          file: updatedFile,
          item: url,
        });
        onContentLoaded({ ...sendObj, url, file: updatedFile });
      }
      removeProgressElement({ id: uploadId });
      this.updateFileCount(-1);
    } else {
      if (shouldUpload) {
        // Uploading asset to our server. only when file has allowed mimetype
        return new Promise((resolve, reject) => {
          this.getSendObject({ file: updatedFile })
            .then(res => {
              let sendObj = res;

              const uploadStartTime = Date.now();

              const { promise } = uploadFile({
                file: updatedFile,
                uploadId,
                startProgress,
                parentType,
                parentId,
                attachment: sendObj,
                portalType,
              });
              promise
                .then(async item => {
                  if (!_.isEmpty(updatedFile)) {
                    sendObj = await this.getSendObject({
                      file: updatedFile,
                      item,
                    });
                    const uploadTime = Date.now() - uploadStartTime;
                    const metadata = _.get(sendObj, "metadata", {});
                    const updatedMetadata = { ...metadata, uploadTime };
                    sendObj = { ...sendObj, metadata: updatedMetadata };
                    if (onContentUploaded) {
                      await onContentUploaded(sendObj);
                    }
                    if (onSingleContentUploaded) {
                      await onSingleContentUploaded(sendObj, uploadId, {
                        parentId,
                        parentType,
                      });
                    }
                  }
                  removeProgressElement({ id: uploadId });
                  this.updateFileCount(-1);
                  resolve(sendObj);
                })
                .catch(e => {
                  //console.log(e);
                  this.updateFileCount(-1, true);
                });
            })
            .catch(reject);
        });
      }
    }
  }

  shouldExportAsLink = mimeType => {
    const { exportAsPdf, enableShareModal } = this.props;

    if (enableShareModal) {
      return false;
    }

    if (_.includes(PICKER_DIRECT_DOWNLOADABLE_TYPES, mimeType)) {
      return false;
    }

    return !exportAsPdf;
  };

  async chooseFiles(data) {
    const {
      enableShareModal,
      excludeFileExtensionsList = [],
      unsupportedFileExtensionError,
      customFilterFilesHandler,
    } = this.props;
    //console.log("enableShareModal", enableShareModal);
    // action is picking a resource
    if (data[window.google.picker.Response.ACTION] == "cancel") {
      this.onClose();
      return;
    }
    let filterFiles = data.docs;

    // filter files based on custom logic
    if (customFilterFilesHandler) {
      filterFiles = customFilterFilesHandler(filterFiles);
    }

    if (_.isEmpty(filterFiles)) {
      this.onClose();
      return;
    }

    const blockMaliciousMimeTypes = ACLStore.can(
      "FeatureFlag:BlockMaliciousMimeType"
    );
    const blockedFileExtensions = blockMaliciousMimeTypes
      ? _.union(excludeFileExtensionsList, BLOCKED_FILE_EXTENSIONS)
      : excludeFileExtensionsList;
    const blockedFileMimeTypes = blockMaliciousMimeTypes
      ? BLOCKED_FILE_MIME_TYPES
      : [];
    const notAllowedFileExtensions = [];
    filterFiles = _.filter(filterFiles, file => {
      const mimeType =
        file?.mimeType || getMimeTypeFromFileName({ fileName: file?.name });

      const extension = `.${
        getFileExtensionFromFilename({
          fileName: file?.name,
        }) || getFileExtensionFromMimeType({ mimeType })
      }`;

      if (
        _.includes(blockedFileExtensions, extension) ||
        _.includes(blockedFileMimeTypes, mimeType)
      ) {
        notAllowedFileExtensions.push(extension);
        return false;
      }
      return true;
    });

    if (_.size(notAllowedFileExtensions) > 0) {
      unsupportedFileExtensionError({
        notAllowedFileExtensions: _.uniq(notAllowedFileExtensions),
        notAllowedFileCount: _.size(notAllowedFileExtensions),
      });
    }

    if (
      data[window.google.picker.Response.ACTION] ==
      window.google.picker.Action.PICKED
    ) {
      if (enableShareModal) {
        this.setState({ showShareModal: true, files: filterFiles });
      } else {
        this.processChosenFiles({ files: filterFiles });
      }
    }
  }

  getThumbUrl = ({ fileId, accessToken, thumbnailSize }) => {
    return `https://drive.google.com/thumbnail?id=${fileId}&access_token=${accessToken}&sz=${thumbnailSize}&blob=true`;
  };

  handleOnMultipleFileUpload = async ({ sendObjList, files }) => {
    const { onMultipleFilesUploaded } = this.props;

    if (_.size(sendObjList) == _.size(files) && onMultipleFilesUploaded) {
      const values = _.filter(await Promise.all(sendObjList), urlObj => urlObj);
      onMultipleFilesUploaded(values);
    }
  };

  customizedFile = async ({ file, uploadId }) => {
    let customFile;
    if (this.props.customizedFile) {
      file.preview = URL.createObjectURL(file);
      this.props.removeProgressElement({ id: uploadId });
      customFile = await this.props.customizedFile(file);
    } else {
      return file;
    }
    return customFile;
  };

  processChosenFiles = ({ files }) => {
    const {
      pickerType,
      onContentLoaded,
      thumbnailSize,
      onDropContent,
      downloadFromUrl,
      parentType,
      parentId,
      enableShareModal,
      t,
      maxSize,
      maxSizeForVideoFiles,
      maxSizeForZipFiles,
      isIncreasedUploadLimit,
    } = this.props;
    const { accessToken } = this.state;

    try {
      // Picker type is youtube
      if (pickerType == "YoutubeVideo") {
        files.map((file, key) => {
          if (onContentLoaded) {
            onContentLoaded(file.url);
          }
        });
      } else {
        //picker type is google drive
        const sendObjList = [];

        //checking file size limit
        let allowedFilesWithSizeLimit = _.filter(files, file => {
          const fileType = file?.mimeType;

          const isLink = enableShareModal
            ? _.isEqual(file.exportAs, "LINK")
            : this.shouldExportAsLink(file.mimeType);
          if (!isLink) {
            const updatedMaxSize = getUpdatedMaxSize({
              maxSize: _.isArray(maxSize)
                ? maxSize
                : _.max(maxSize, maxSizeForVideoFiles, maxSizeForZipFiles),
              fileType,
            });

            return _.isNumber(updatedMaxSize)
              ? updatedMaxSize >= _.get(file, "sizeBytes", 0)
              : true;
          }
          return true;
        });

        if (!_.isArray(maxSize)) {
          const oversizedFiles = _.filter(allowedFilesWithSizeLimit, file => {
            const isLink = enableShareModal
              ? _.isEqual(file.exportAs, "LINK")
              : this.shouldExportAsLink(file.mimeType);
            if (isLink) {
              return false;
            }
            let maxSizeForFile = maxSize;
            if (
              is500MBVideoUploadLimitEnabled() &&
              file?.mimeType?.startsWith("video/")
            ) {
              maxSizeForFile = maxSizeForVideoFiles;
            }

            if (
              isIncreasedUploadLimit &&
              _.includes(ARCHIVE_MIME_TYPES, file?.mimeType)
            ) {
              maxSizeForFile = maxSizeForZipFiles;
            }

            return maxSizeForFile < _.get(file, "sizeBytes", 0);
          });

          if (!_.isEmpty(oversizedFiles)) {
            allowedFilesWithSizeLimit = _.filter(
              allowedFilesWithSizeLimit,
              file => {
                return !_.some(
                  oversizedFiles,
                  oversizedFile =>
                    file.name === oversizedFile.name &&
                    file.size === oversizedFile.size &&
                    file.lastModified === oversizedFile.lastModified
                );
              }
            );
          }
        }

        allowedFilesWithSizeLimit.map(async (file, key) => {
          this.updateFileCount(1);

          const fileUploadId = generateRandomId();
          const fileDetails = await getGoogleFileDetails({
            fileId: file.id,
            accessToken,
          });

          const thumbUrl = _.get(fileDetails, "data.thumbnailLink");
          const largerThumbnailUrl = thumbUrl?.replace("s220", "s2048");
          const isLink = enableShareModal
            ? _.isEqual(file.exportAs, "LINK")
            : this.shouldExportAsLink(file.mimeType);

          let sendObj = await this.getSendObject({
            file,
            item: thumbUrl,
            isLink,
          });
          //Sending this metadata to for BE to download and upload to S3.
          //Sending this with object as thumbUrl with to show preview while creation.
          if (largerThumbnailUrl) {
            sendObj.metadata.tempThumbUrl = largerThumbnailUrl;
            sendObj.thumbUrl = largerThumbnailUrl;
          }
          if (onDropContent) {
            // Here we have either thumb-url or link url(actual file url) as file url
            onDropContent({ ...sendObj, id: fileUploadId });
          }

          if (!isLink) {
            // File is not of type link, i.e may be exportAsPdf is true or it is inside the allowed mimetype
            const fileUrl = enableShareModal
              ? file.url
              : await getExportableFileUrl({
                  file,
                  accessToken,
                });
            // Starting the download of file from google drive.
            downloadFromUrl({
              size: file.sizeBytes,
              downloadUrl: fileUrl,
              name: file.name,
              parentType: parentType,
              parentId: parentId,
              attachment: sendObj,
              headers: [
                { label: "Authorization", value: `Bearer ${accessToken}` },
              ],
              fileUploadId: fileUploadId,
              showErrorMessage: true,
              onContentLoadFailed: async ({ isCancelled }) => {
                if (!isCancelled && !enableShareModal) {
                  sendObj = await this.getSendObject({
                    file,
                    item: thumbUrl,
                    isLink: true,
                  });
                  this.onContentLoaded({ sendObj, fileUploadId });
                } else {
                  this.updateFileCount(-1, true);
                }
              },
              onLoaded: async fileBlob => {
                if (_.isNil(fileBlob)) {
                  return;
                }

                if (fileBlob) {
                  fileBlob.name = _.get(file, "name", "Untitled");
                  if (!fileBlob.type) {
                    fileBlob.mimeType = _.get(file, "mimeType");
                  }
                }

                fileBlob = await this.customizedFile({
                  file: fileBlob,
                  uploadId: fileUploadId,
                });

                const sendObj = this.onFileDownloadedFromDrive({
                  file: fileBlob,
                  uploadId: fileUploadId,
                  startProgress: START_PROGRESS,
                });
                sendObjList.push(sendObj);

                // Sends all files' sendObj together as an array
                this.handleOnMultipleFileUpload({
                  sendObjList,
                  files: allowedFilesWithSizeLimit,
                });
              },
              startProgress: START_PROGRESS,
            });
          } else {
            this.onContentLoaded({ sendObj, fileUploadId });
            sendObjList.push(sendObj);

            this.handleOnMultipleFileUpload({
              sendObjList,
              files: allowedFilesWithSizeLimit,
            });
          }
        });

        if (_.size(files) > _.size(allowedFilesWithSizeLimit)) {
          const fileTypesAndMaxSizes = getFileTypesAndMaxSizes({
            files,
            allowedFiles: allowedFilesWithSizeLimit,
            fileTypePropName: "mimeType",
            maxSize: this.props.maxSize,
            maxSizeForVideoFiles: this.props.maxSizeForVideoFiles,
            maxSizeForZipFiles: this.props.maxSizeForZipFiles,
            is500MBVideoLimitEnabled: is500MBVideoUploadLimitEnabled(),
            isIncreasedUploadLimit: this.props.isIncreasedUploadLimit,
          });

          this.props.exceededSizeError({
            fileTypesAndMaxSizes,
          });
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  onContentLoaded = ({ sendObj, fileUploadId }) => {
    const {
      parentId,
      parentType,
      onSingleContentUploaded,
      onContentUploaded,
      onContentLoaded,
    } = this.props;

    if (onContentLoaded) {
      onContentLoaded(sendObj);
    }
    // we have a link of file
    if (onContentUploaded) {
      onContentUploaded(sendObj);
    }
    if (onSingleContentUploaded) {
      onSingleContentUploaded(sendObj, fileUploadId, {
        parentId,
        parentType,
      });
    }
    this.updateFileCount(-1);
  };

  onClose = () => {
    const { onClose } = this.props;

    if (onClose) {
      onClose();
    }
  };

  toggleSharingFormatModal = () => {
    const { showShareModal } = this.state;

    if (showShareModal) {
      this.onClose();
    }

    this.setState({ showShareModal: !showShareModal });
  };

  getMimeTypesArray = ({ accept }) => {
    if (!(_.isNull(accept) || _.isUndefined(accept)) && _.isString(accept)) {
      return _.split(accept, ",");
    }
    return accept || [];
  };

  render() {
    const {
      enableShareModal,
      renderComponent,
      classStyle,
      multiselect,
      navHidden,
      authImmediate,
      pickerType,
      onClickEvent,
      enableShareDrive,
      sourceType,
      accept,
      setInnerGooglePickerRef,
      defaultExportType,
      attachmentExportOptions,
      userType,
      disabled,
      shouldStopPropagation,
    } = this.props;
    const { files, showShareModal, accessToken } = this.state;
    const mimeTypes = this.getMimeTypesArray({ accept });

    return (
      <React.Fragment>
        <GooglePickerChooser
          clientId={GOOGLE_CLIENT_ID}
          developerKey={GOOGLE_DEVELOPER_KEY}
          onChange={data => {
            this.chooseFiles(data);
          }}
          mimeTypes={mimeTypes}
          multiselect={multiselect}
          navHidden={navHidden}
          authImmediate={authImmediate}
          pickerType={pickerType}
          viewId={"DOCS"}
          classStyle={classStyle}
          onClickEvent={onClickEvent}
          enableShareDrive={enableShareDrive}
          updateAccessToken={this.updateAccessToken}
          setInnerGooglePickerRef={setInnerGooglePickerRef}
          disabled={disabled}
          userType={userType}
          shouldStopPropagation={shouldStopPropagation}
        >
          {renderComponent ? (
            renderComponent
          ) : (
            <div className={classStyle}>
              <GoogleDriveIcon fill={colors.neutral400} />
            </div>
          )}
        </GooglePickerChooser>
        {enableShareModal && showShareModal && (
          <div
            onClick={e => {
              e.stopPropagation();
            }}
          >
            <DriveShareModal
              serviceType={"GOOGLE"}
              processChosenFiles={this.processChosenFiles}
              onClickClose={this.toggleSharingFormatModal}
              files={files}
              accessToken={accessToken}
              sourceType={sourceType}
              defaultExportType={defaultExportType}
              attachmentExportOptions={attachmentExportOptions}
              userType={userType}
              customStyle={{ zIndex: 1151 }}
            />
          </div>
        )}
      </React.Fragment>
    );
  }
}

const mapActionCreators = {
  uploadFile,
  setProgress,
  downloadFromUrl,
  removeProgressElement,
  exceededSizeError,
  heicToJpg,
  setToastMsg,
  unsupportedFileExtensionError,
};

const mapStateToProps = (state, ownProps) => {
  let { maxSize } = ownProps;
  const { isIncreasedUploadLimit = false } = ownProps;
  const isCurriculumProgramFree = getCurriculumProgramFreeStatus({ state });
  const allowInfiniteFileSize = ACLStore.can(
    "FeatureFlag:DropzoneInfiniteFileSize"
  );

  const maxSizeForVideoFiles = getMaxSizeForVideoFiles({
    maxSize,
    isCurriculumProgramFree,
    is500MBVideoLimit: is500MBVideoUploadLimitEnabled(),
    allowInfiniteFileSize,
  });

  const maxSizeForZipFiles = getMaxSizeForZipFiles({
    maxSize,
    isCurriculumProgramFree,
    allowInfiniteFileSize,
    isIncreasedUploadLimit,
  });

  maxSize = getMaxSize({
    maxSize,
    isCurriculumProgramFree,
    allowInfiniteFileSize,
  });
  return {
    maxSize,
    maxSizeForVideoFiles,
    maxSizeForZipFiles,
    userType: _.get(state, "login.userInfo.user_type"),
    isCurriculumProgramFree,
  };
};

GooglePicker.propTypes = {
  accept: PropTypes.array,
  enableShareModal: PropTypes.bool,
  onContentLoaded: PropTypes.func, //handle content upload on your own
  onContentUploaded: PropTypes.func,
  onContentUploadProgress: PropTypes.func,
  updateFilesCount: PropTypes.func,
  customizedFile: PropTypes.func,
  renderComponent: PropTypes.element,
  multiselect: PropTypes.bool,
  navHidden: PropTypes.bool,
  authImmediate: PropTypes.bool,
  onClickEvent: PropTypes.func,
  onSingleContentUploaded: PropTypes.func,
  onDropContent: PropTypes.func,
  pickerType: PropTypes.oneOf(["GoogleDrive", "YoutubeVideo"]),
  thumbnailSize: PropTypes.string,
  parentId: PropTypes.string,
  parentType: PropTypes.string,
  enableShareDrive: PropTypes.bool,
  exportAsPdf: PropTypes.bool,
  sourceType: PropTypes.oneOf([
    "RESOURCE",
    "STUDENT_TEMPLATE",
    "STUDENT_FILES",
  ]),
  excludeFileExtensionsList: PropTypes.array,
  customFilterFilesHandler: PropTypes.func,
  shouldStopPropagation: PropTypes.bool,
};

GooglePicker.defaultProps = {
  accept: [],
  shouldUpload: true,
  multiselect: true,
  navHidden: true,
  authImmediate: false,
  pickerType: "GoogleDrive",
  thumbnailSize: "w500-h2500",
  parentType: "",
  parentId: null,
  enableShareDrive: true,
  exportAsPdf: false,
  enableShareModal: true,
  sourceType: "RESOURCE",
  excludeFileExtensionsList: [],
  shouldStopPropagation: true,
};

export default compose(
  I18nHOC({ resource: ["common"] }),
  connect(mapStateToProps, mapActionCreators)
)(GooglePicker);
